import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  SignOut, ArrowClockwise, TrashSimple, DownloadSimple, CaretDown, FloppyDisk,
  UploadSimple, ImageSquare, Users, PencilSimple, EnvelopeSimple, Phone, MapPin, FileText,
} from "@phosphor-icons/react";
import { api, fileUrl, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { STATUS_OPTIONS, DEFAULT_CONTENT } from "@/data/defaults";

const statusStyles = {
  New: "bg-gray-900 text-white",
  "In Progress": "bg-gray-200 text-gray-900",
  Won: "bg-emerald-600 text-white",
  Lost: "bg-red-600 text-white",
  Archived: "bg-gray-100 text-gray-500",
};

function LeadCard({ lead, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(lead.notes || "");
  const [status, setStatus] = useState(lead.status || "New");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate(lead.id, { status, notes });
      toast.success("Lead updated");
    } catch {
      toast.error("Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const files = [...(lead.photos || []), ...(lead.documents || [])];

  return (
    <div className="border border-gray-200 bg-white" data-testid={`lead-${lead.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-gray-50 transition-colors duration-200"
        data-testid={`lead-toggle-${lead.id}`}
      >
        <div className="min-w-0">
          <p className="font-heading font-semibold text-gray-900 truncate">{lead.full_name}</p>
          <p className="text-sm text-gray-500 truncate">{lead.email} · {lead.phone}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusStyles[lead.status] || statusStyles.New}`}>
            {lead.status || "New"}
          </span>
          <CaretDown size={18} className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-200 p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <Detail label="Project Address" value={lead.project_address} />
            <Detail label="Planned Budget" value={lead.budget} />
            <Detail label="Heard About Us" value={lead.hear_about_us} />
            <Detail label="Submitted" value={new Date(lead.created_at).toLocaleString()} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Project Details</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.project_details}</p>
          </div>

          {files.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Attachments</p>
              <div className="flex flex-wrap gap-2">
                {files.map((f) => (
                  <a
                    key={f.id}
                    href={fileUrl(f.storage_path)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:border-gray-900 transition-colors duration-200"
                    data-testid={`lead-file-${f.id}`}
                  >
                    <DownloadSimple size={16} /> {f.original_filename}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid={`lead-status-${lead.id}`}
                className="mt-1 w-full border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:border-gray-900"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Private Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid={`lead-notes-${lead.id}`}
              className="mt-1 w-full border border-gray-300 rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:border-gray-900"
              placeholder="Internal notes about this lead..."
            />
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => onDelete(lead.id)}
              data-testid={`lead-delete-${lead.id}`}
              className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors duration-200"
            >
              <TrashSimple size={16} /> Delete
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid={`lead-save-${lead.id}`}
              className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-5 py-2.5 rounded-sm hover:bg-gray-700 transition-colors duration-200 disabled:opacity-60"
            >
              <FloppyDisk size={16} /> {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-0.5">{label}</p>
    <p className="text-gray-800">{value || "-"}</p>
  </div>
);

function Field({ label, value, onChange, textarea, rows = 3, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">{label}</label>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          data-testid={testid}
          className="mt-1 w-full border border-gray-300 rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:border-gray-900"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="mt-1 w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gray-900"
        />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/content/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-400">{label}</label>
      <div className="mt-1 flex items-center gap-3">
        <div className="h-16 w-24 border border-gray-200 bg-gray-50 overflow-hidden shrink-0">
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <ImageSquare size={24} className="m-auto mt-5 text-gray-300" />}
        </div>
        <label className="inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-sm cursor-pointer hover:border-gray-900 transition-colors duration-200" data-testid={testid}>
          <UploadSimple size={16} /> {uploading ? "Uploading..." : "Replace"}
          <input type="file" accept="image/*" hidden onChange={upload} />
        </label>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const { content, setContent, refresh } = useContent();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [draft, setDraft] = useState(content);
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => { setDraft(content); }, [content]);

  const loadLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const { data } = await api.get("/consultations");
      setLeads(data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const updateLead = async (id, fields) => {
    const { data } = await api.patch(`/consultations/${id}`, fields);
    setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/consultations/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const setPath = (section, key, val) =>
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: val } }));

  const setService = (idx, key, val) =>
    setDraft((d) => ({
      ...d,
      services: d.services.map((s, i) => (i === idx ? { ...s, [key]: val } : s)),
    }));

  const setStat = (idx, key, val) =>
    setDraft((d) => ({
      ...d,
      stats: d.stats.map((s, i) => (i === idx ? { ...s, [key]: val } : s)),
    }));

  const saveContent = async () => {
    setSavingContent(true);
    try {
      const { data } = await api.put("/content", draft);
      setContent({ ...DEFAULT_CONTENT, ...data });
      await refresh();
      toast.success("Website content saved");
    } catch {
      toast.error("Failed to save content");
    } finally {
      setSavingContent(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-gray-950 text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-white.png" alt="Mountain Core" className="h-8 w-auto" />
            <span className="hidden sm:inline text-xs uppercase tracking-[0.25em] text-gray-500">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-gray-400">{user?.email}</span>
            <button onClick={logout} data-testid="logout-button" className="inline-flex items-center gap-2 text-sm border border-gray-700 px-4 py-2 rounded-sm hover:bg-white hover:text-gray-950 transition-colors duration-200">
              <SignOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-8 border-b border-gray-200">
          {[
            { id: "leads", label: "Consultation Leads", Icon: Users },
            { id: "content", label: "Website Content", Icon: PencilSimple },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`tab-${id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors duration-200 ${
                tab === id ? "border-gray-900 text-gray-900" : "border-transparent text-gray-400 hover:text-gray-700"
              }`}
            >
              <Icon size={18} /> {label}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div data-testid="leads-panel">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-gray-500">{leads.length} total request{leads.length === 1 ? "" : "s"}</p>
              <button onClick={loadLeads} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200" data-testid="refresh-leads">
                <ArrowClockwise size={16} /> Refresh
              </button>
            </div>
            {loadingLeads ? (
              <p className="text-gray-400 text-sm py-12 text-center">Loading leads...</p>
            ) : leads.length === 0 ? (
              <div className="border border-dashed border-gray-300 py-16 text-center">
                <FileText size={32} className="mx-auto text-gray-300" />
                <p className="mt-3 text-gray-500 text-sm">No consultation requests yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map((l) => (
                  <LeadCard key={l.id} lead={l} onUpdate={updateLead} onDelete={deleteLead} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "content" && (
          <div data-testid="content-panel" className="space-y-10 pb-24">
            <Section title="Hero Section">
              <Field label="Label" value={draft.hero?.label} onChange={(v) => setPath("hero", "label", v)} testid="content-hero-label" />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Headline Line 1" value={draft.hero?.title_line1} onChange={(v) => setPath("hero", "title_line1", v)} testid="content-hero-line1" />
                <Field label="Headline Line 2" value={draft.hero?.title_line2} onChange={(v) => setPath("hero", "title_line2", v)} testid="content-hero-line2" />
              </div>
              <Field label="Subtext" textarea value={draft.hero?.subtext} onChange={(v) => setPath("hero", "subtext", v)} testid="content-hero-subtext" />
              <ImageField label="Hero Image" value={draft.hero?.image} onChange={(v) => setPath("hero", "image", v)} testid="content-hero-image" />
            </Section>

            <Section title="Stats">
              <div className="grid sm:grid-cols-3 gap-4">
                {(draft.stats || []).map((s, i) => (
                  <div key={i} className="space-y-2 border border-gray-200 p-3">
                    <Field label="Value" value={s.value} onChange={(v) => setStat(i, "value", v)} testid={`content-stat-value-${i}`} />
                    <Field label="Label" value={s.label} onChange={(v) => setStat(i, "label", v)} testid={`content-stat-label-${i}`} />
                  </div>
                ))}
              </div>
            </Section>

            <Section title="About">
              <Field label="Heading" textarea rows={2} value={draft.about?.heading} onChange={(v) => setPath("about", "heading", v)} testid="content-about-heading" />
              <Field label="Paragraph 1" textarea value={draft.about?.para1} onChange={(v) => setPath("about", "para1", v)} testid="content-about-para1" />
              <Field label="Paragraph 2" textarea value={draft.about?.para2} onChange={(v) => setPath("about", "para2", v)} testid="content-about-para2" />
              <Field label="Leadership Heading" value={draft.about?.leadership_heading} onChange={(v) => setPath("about", "leadership_heading", v)} testid="content-about-lh" />
              <Field label="Leadership Text" textarea value={draft.about?.leadership} onChange={(v) => setPath("about", "leadership", v)} testid="content-about-leadership" />
              <div className="grid sm:grid-cols-2 gap-4">
                <ImageField label="About Image 1" value={draft.about?.image1} onChange={(v) => setPath("about", "image1", v)} testid="content-about-image1" />
                <ImageField label="About Image 2" value={draft.about?.image2} onChange={(v) => setPath("about", "image2", v)} testid="content-about-image2" />
              </div>
            </Section>

            <Section title="Services">
              <div className="space-y-4">
                {(draft.services || []).map((s, i) => (
                  <div key={s.id} className="border border-gray-200 p-4 space-y-3">
                    <Field label="Title" value={s.title} onChange={(v) => setService(i, "title", v)} testid={`content-service-title-${i}`} />
                    <Field label="Description" textarea rows={2} value={s.desc} onChange={(v) => setService(i, "desc", v)} testid={`content-service-desc-${i}`} />
                    <ImageField label="Photo" value={s.image} onChange={(v) => setService(i, "image", v)} testid={`content-service-image-${i}`} />
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Contact Info">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" value={draft.contact?.email} onChange={(v) => setPath("contact", "email", v)} testid="content-contact-email" />
                <Field label="Phone" value={draft.contact?.phone} onChange={(v) => setPath("contact", "phone", v)} testid="content-contact-phone" />
                <Field label="Coverage" value={draft.contact?.coverage} onChange={(v) => setPath("contact", "coverage", v)} testid="content-contact-coverage" />
                <Field label="Response Time" value={draft.contact?.response_time} onChange={(v) => setPath("contact", "response_time", v)} testid="content-contact-response" />
              </div>
            </Section>

            <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-gray-200 py-4">
              <div className="max-w-6xl mx-auto px-6 flex justify-end">
                <button
                  onClick={saveContent}
                  disabled={savingContent}
                  data-testid="save-content-button"
                  className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-7 py-3 rounded-sm hover:bg-gray-700 transition-colors duration-200 disabled:opacity-60"
                >
                  <FloppyDisk size={18} /> {savingContent ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="border border-gray-200 bg-white p-6">
    <h2 className="font-heading text-lg font-semibold text-gray-900 mb-5">{title}</h2>
    <div className="space-y-4">{children}</div>
  </section>
);
